import {
  fromUserInputToFormValue,
  MessageFormValue,
  MessageFormValueSchema,
  toMessageUserInput,
} from '../model/messageForm';
import { useLocalStorage } from '@mantine/hooks';
import { produce } from 'immer';
import { ActionSetting } from '../model';
import { useSearchParams } from 'react-router-dom';

const PARAMS_KEY = 'preset';
const applyParams = (path: string) => {
  const url = new URL(path, location.href);
  url.searchParams.append(PARAMS_KEY, '');

  return url.toString();
};
export const useWithPresetParam = () => {
  const [value] = useSearchParams();

  const shouldWithPreset = typeof value.get(PARAMS_KEY) === 'string';

  return { shouldWithPreset, applyParams };
};

const MESSAGE_CREATE_PRESET_STORAGE_KEY = 'message:create_preset';

export const useMessageCreatePresetStorage = () => {
  const [preset, setPreset] = useLocalStorage<MessageFormValue | undefined>({
    key: MESSAGE_CREATE_PRESET_STORAGE_KEY,
    serialize: (value) => {
      if (value === undefined) {
        return '';
      }

      const {
        success,
        data: validValue,
        error,
      } = MessageFormValueSchema.safeParse(value);

      if (!success) {
        console.error(error);

        return '';
      }

      const { payload, files } = toMessageUserInput(validValue);

      // the key usage here should align with toMessageUserInput transformer
      const isUploadRef = (s: string | null): s is string =>
        typeof s === 'string' && s.startsWith('@upload:');

      const dropUploadRefs = (action: ActionSetting) => {
        switch (action.type) {
          case 'action_list_dialog':
            if (isUploadRef(action.attribute_schema.image)) {
              action.attribute_schema.image = '';
            }
            action.attribute_schema.actions.forEach(({ action }) =>
              dropUploadRefs(action),
            );
            return;
          case 'alert_dialog':
          case 'full_screen_dialog':
            if (isUploadRef(action.attribute_schema.image)) {
              action.attribute_schema.image = '';
            }
            dropUploadRefs(action.attribute_schema.action);
            return;
          case 'message_box':
            if (isUploadRef(action.attribute_schema.image)) {
              action.attribute_schema.image = '';
            }
            return;
          case 'open_link':
            return;
        }
      };

      const cleaned = produce(payload, (draft) => {
        dropUploadRefs(draft.actionSetting);

        if (files.thumbFile) {
          draft.thumbUrl = '';
        }
      });

      const str = JSON.stringify(cleaned);

      return str;
    },
    deserialize: (localStorageValue) => {
      try {
        if (localStorageValue === undefined) {
          throw 'no value';
        }
        const json = JSON.parse(localStorageValue);

        const formValue = fromUserInputToFormValue(json);

        // cleanup the scheduleAt, cuz its would always the past value in preset
        formValue.scheduleAt = undefined;

        return formValue;
      } catch (err) {
        console.error(err);
        return undefined;
      }
    },
  });

  return {
    preset,
    setPreset,
  };
};
